import { useState } from 'react';
import { Spinner } from '@klaro7/ui';
import { UserRole, type Payout } from '@klaro7/shared-types';

import {
  useEarningsSummary,
  useCreatePayoutRun,
  useMarkPayoutPaid,
  useCancelPayout,
  useAllPayouts,
  useVerifySignedReceipt,
  fetchAdminPayoutDocBlob,
  type AdminPayoutDocKind,
  type AdminPayoutListItem,
} from './api';
import {
  DateRangeFilter,
  FILTER_SELECT_CLS,
  ReceiptStatusSelect,
  ResetFiltersButton,
  SearchFilterInput,
  SignedReceiptBadge,
} from './filter-controls';
import { useDebouncedSearch, useUrlFilters } from './useUrlFilters';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { extractErrorMessage } from '@/lib/api';
import { downloadBlob } from '@/lib/download';

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Per-row mark-paid inline form ─────────────────────────────────────────────
interface MarkPaidFormProps {
  payout: Payout;
  onDismiss: () => void;
}

function MarkPaidForm({ payout, onDismiss }: MarkPaidFormProps) {
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const markPaid = useMarkPayoutPaid();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reference.trim()) {
      setError('Inserisci il riferimento del bonifico');
      return;
    }
    setError(null);
    markPaid.mutate(
      { payoutId: payout.id, paymentReference: reference.trim() },
      {
        onSuccess: () => {
          onDismiss();
        },
        onError: (err) => {
          setError(extractErrorMessage(err));
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2">
      <div className="text-xs text-neutral-500">
        Payout <span className="font-mono text-neutral-700">{payout.id.slice(0, 8)}…</span>
        {' · '}{formatEur(payout.totalGross)} ({payout.earningsCount} compensi)
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Riferimento bonifico"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className="h-8 flex-1 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-teal/50 focus:outline-none"
        />
        <button
          type="submit"
          disabled={markPaid.isPending}
          className="h-8 rounded-lg bg-brand-teal px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-teal/90 disabled:opacity-50"
        >
          {markPaid.isPending ? <Spinner size="sm" /> : 'Conferma'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={markPaid.isPending}
          className="h-8 rounded-lg border border-neutral-200 px-3 text-xs text-neutral-600 transition-colors hover:bg-neutral-50 disabled:opacity-50"
        >
          Annulla
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </form>
  );
}

// ── Status badge + invoice download + compensi list ───────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending_confirmation: { label: 'Da confermare (prof.)', cls: 'bg-blue-100 text-blue-700' },
    pending_payment: { label: 'Da pagare', cls: 'bg-amber-100 text-amber-700' },
    paid: { label: 'Pagato', cls: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Annullato', cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = map[status] ?? { label: status, cls: 'bg-neutral-100 text-neutral-600' };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function docFilename(payout: AdminPayoutListItem, kind: AdminPayoutDocKind): string {
  switch (kind) {
    case 'invoice':
      return payout.invoice?.originalName ?? `fattura-${payout.id}.pdf`;
    case 'signed-receipt':
      return payout.signedReceipt?.originalName ?? `ricevuta-firmata-${payout.receiptNumber ?? payout.id}.pdf`;
    case 'receipt':
      return `ricevuta-${payout.receiptNumber ?? payout.id}.pdf`;
    case 'proforma':
      return `proforma-${payout.proformaNumber ?? payout.id}.pdf`;
  }
}

/** Download di un documento del compenso (fattura, ricevuta, proforma, ricevuta firmata). */
function DocDownloadButton({
  payout,
  kind,
  label,
}: {
  payout: AdminPayoutListItem;
  kind: AdminPayoutDocKind;
  label: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const handle = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const blob = await fetchAdminPayoutDocBlob(payout.id, kind);
      downloadBlob(blob, docFilename(payout, kind));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => void handle()}
      disabled={busy}
      title={failed ? 'Download non riuscito, riprova' : undefined}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium disabled:opacity-60 ${
        failed ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
      }`}
    >
      {busy ? <Spinner size="sm" /> : '📎'} {label}
    </button>
  );
}

/** Super-admin: segna la ricevuta firmata come verificata (o toglie la verifica). */
function VerifySignedReceiptToggle({ payout }: { payout: AdminPayoutListItem }) {
  const verify = useVerifySignedReceipt();
  const [error, setError] = useState<string | null>(null);
  const verified = payout.receiptStatus === 'verified';
  return (
    <div className="flex flex-col items-center gap-0.5">
      <button
        type="button"
        onClick={() => {
          setError(null);
          verify.mutate(
            { payoutId: payout.id, verified: !verified },
            { onError: (err) => setError(extractErrorMessage(err)) }
          );
        }}
        disabled={verify.isPending}
        className={`whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${
          verified
            ? 'border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
            : 'border border-green-200 text-green-700 hover:bg-green-50'
        }`}
      >
        {verify.isPending ? <Spinner size="sm" /> : verified ? 'Togli verifica' : 'Segna verificata'}
      </button>
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: 'pending_payment', label: 'Da pagare' },
  { key: 'pending_confirmation', label: 'In attesa professionista' },
  { key: 'paid', label: 'Pagati' },
  { key: 'cancelled', label: 'Annullati' },
  { key: '', label: 'Tutti' },
];

/** Stati in cui il denaro non è ancora uscito: solo da qui si può annullare. */
const CANCELLABLE_STATUSES = ['pending_confirmation', 'pending_payment'];

/**
 * Annullamento di una liquidazione sbagliata. I compensi agganciati tornano
 * "in sospeso" e rientrano nella prossima generazione; la motivazione resta
 * registrata sul payout. Lato API è riservato al super-admin.
 */
function AdminCancelInline({ payout }: { payout: AdminPayoutListItem }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelPayout = useCancelPayout();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
      >
        Annulla
      </button>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason.trim()) { setError('Indica il motivo dell\'annullamento'); return; }
        setError(null);
        cancelPayout.mutate(
          { payoutId: payout.id, reason: reason.trim() },
          { onSuccess: () => setOpen(false), onError: (err) => setError(extractErrorMessage(err)) }
        );
      }}
      className="flex flex-col gap-1"
    >
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          placeholder="Motivo annullamento"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          className="h-8 w-40 rounded-lg border border-neutral-200 px-2 text-sm focus:border-red-400 focus:outline-none"
        />
        <button type="submit" disabled={cancelPayout.isPending} className="h-8 rounded-lg bg-red-600 px-2.5 text-xs font-semibold text-white disabled:opacity-50">
          {cancelPayout.isPending ? <Spinner size="sm" /> : 'OK'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="h-8 rounded-lg border border-neutral-200 px-2 text-xs text-neutral-600">✕</button>
      </div>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </form>
  );
}

function AdminMarkPaidInline({ payout }: { payout: AdminPayoutListItem }) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const markPaid = useMarkPayoutPaid();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-lg bg-brand-teal px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-teal/90"
      >
        Segna pagato
      </button>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reference.trim()) { setError('Inserisci il riferimento del bonifico'); return; }
        setError(null);
        markPaid.mutate(
          { payoutId: payout.id, paymentReference: reference.trim() },
          { onSuccess: () => setOpen(false), onError: (err) => setError(extractErrorMessage(err)) }
        );
      }}
      className="flex flex-col gap-1"
    >
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          placeholder="Rif. bonifico"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className="h-8 w-32 rounded-lg border border-neutral-200 px-2 text-sm focus:border-brand-teal/50 focus:outline-none"
        />
        <button type="submit" disabled={markPaid.isPending} className="h-8 rounded-lg bg-brand-teal px-2.5 text-xs font-semibold text-white disabled:opacity-50">
          {markPaid.isPending ? <Spinner size="sm" /> : 'OK'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="h-8 rounded-lg border border-neutral-200 px-2 text-xs text-neutral-600">✕</button>
      </div>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </form>
  );
}

const ADMIN_FILTER_DEFAULTS = {
  status: 'pending_payment',
  q: '',
  professionalId: '',
  documentType: '',
  receiptStatus: '',
  from: '',
  to: '',
  minAmount: '',
  maxAmount: '',
  page: '1',
};

const ADMIN_PAGE_SIZE = 50;

function CompensiList() {
  const { values: f, setFilters, reset, isDirty } = useUrlFilters(ADMIN_FILTER_DEFAULTS);
  // Ogni cambio di filtro riparte da pagina 1.
  const applyFilters = (patch: Partial<typeof ADMIN_FILTER_DEFAULTS>) => setFilters({ ...patch, page: '1' });
  const [search, setSearch] = useDebouncedSearch(f.q, (q) => applyFilters({ q }));

  const page = Math.max(1, Number(f.page) || 1);
  const { data, isLoading, isFetching } = useAllPayouts({
    status: f.status,
    q: f.q,
    professionalId: f.professionalId,
    documentType: f.documentType,
    receiptStatus: f.receiptStatus,
    from: f.from,
    to: f.to,
    minAmount: f.minAmount,
    maxAmount: f.maxAmount,
    page,
    limit: ADMIN_PAGE_SIZE,
  });
  const payouts = data?.items;
  const pagination = data?.pagination;
  const hasExtraFilters = Boolean(
    f.q || f.professionalId || f.documentType || f.receiptStatus || f.from || f.to || f.minAmount || f.maxAmount
  );

  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole(UserRole.SUPER_ADMIN);
  const canCancel = isSuperAdmin;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">Compensi</h2>
          <p className="mt-0.5 text-sm text-neutral-500">Proforma e ritenute generate, conferme dei professionisti e pagamenti.</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key || 'all'}
              onClick={() => applyFilters({ status: s.key })}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                f.status === s.key ? 'bg-brand-teal text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-5 py-3">
        <SearchFilterInput
          value={search}
          onChange={setSearch}
          placeholder="Cerca professionista, email o n. documento"
        />
        <select
          value={f.documentType}
          onChange={(e) => applyFilters({ documentType: e.target.value })}
          aria-label="Filtra per documento"
          className={FILTER_SELECT_CLS}
        >
          <option value="">Tutti i documenti</option>
          <option value="ritenuta">Ricevuta (ritenuta)</option>
          <option value="proforma">Proforma</option>
        </select>
        <ReceiptStatusSelect value={f.receiptStatus} onChange={(receiptStatus) => applyFilters({ receiptStatus })} />
        <DateRangeFilter from={f.from} to={f.to} onChange={applyFilters} />
        <div className="flex items-center gap-1.5 text-sm text-neutral-500">
          <span>Lordo €</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            placeholder="min"
            value={f.minAmount}
            onChange={(e) => applyFilters({ minAmount: e.target.value })}
            aria-label="Importo lordo minimo"
            className={`${FILTER_SELECT_CLS} w-24`}
          />
          <span>–</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            placeholder="max"
            value={f.maxAmount}
            onChange={(e) => applyFilters({ maxAmount: e.target.value })}
            aria-label="Importo lordo massimo"
            className={`${FILTER_SELECT_CLS} w-24`}
          />
        </div>
        <ResetFiltersButton onClick={reset} disabled={!isDirty} />
        {f.professionalId && (
          <button
            type="button"
            onClick={() => applyFilters({ professionalId: '' })}
            className="inline-flex h-7 items-center gap-1 rounded-full bg-brand-teal/10 px-3 text-xs font-medium text-brand-teal hover:bg-brand-teal/20"
          >
            Un solo professionista ✕
          </button>
        )}
        {pagination && (
          <span className="text-sm text-neutral-400">
            {pagination.total} compens{pagination.total === 1 ? 'o' : 'i'}
            {isFetching && !isLoading ? ' · aggiornamento…' : ''}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : !payouts || payouts.length === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-500">
          {hasExtraFilters ? 'Nessun compenso con questi filtri.' : 'Nessun compenso in questo stato.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 bg-neutral-50 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3 text-left">Professionista</th>
                <th className="px-4 py-3 text-left">Periodo</th>
                <th className="px-4 py-3 text-right">Lordo</th>
                <th className="px-4 py-3 text-right">Netto</th>
                <th className="px-4 py-3 text-center">Documento</th>
                <th className="px-4 py-3 text-center">Stato</th>
                <th className="px-4 py-3 text-center">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {payouts.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-neutral-800">
                    <button
                      type="button"
                      onClick={() => applyFilters({ professionalId: p.professionalId })}
                      className="text-left hover:text-brand-teal hover:underline"
                      title="Mostra solo i compensi di questo professionista"
                    >
                      {p.professionalName}
                    </button>
                    {p.professionalEmail && <div className="text-xs text-neutral-500">{p.professionalEmail}</div>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {formatDate(p.periodFrom)} → {formatDate(p.periodTo)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-neutral-900">{formatEur(p.totalGross)}</td>
                  <td className="px-4 py-3 text-right text-neutral-700">{formatEur(p.netto)}</td>
                  <td className="px-4 py-3 text-center text-neutral-600">
                    <div>
                      {p.documentType === 'proforma'
                        ? `Proforma${p.proformaNumber != null ? ` #${p.proformaNumber}` : ''}`
                        : `Ricevuta${p.receiptNumber != null ? ` #${p.receiptNumber}` : ''}`}
                    </div>
                    {p.documentType === 'ritenuta' && (
                      <div className="mt-1">
                        <SignedReceiptBadge status={p.receiptStatus ?? (p.signedReceipt ? 'uploaded' : 'missing')} />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={p.status} />
                    {p.status === 'cancelled' && p.cancellationReason && (
                      <p className="mt-1 text-[11px] text-neutral-500">{p.cancellationReason}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {p.documentType === 'proforma' && p.hasProforma && (
                        <DocDownloadButton payout={p} kind="proforma" label="Proforma" />
                      )}
                      {p.documentType === 'ritenuta' && p.hasReceipt && (
                        <DocDownloadButton payout={p} kind="receipt" label="Ricevuta" />
                      )}
                      {p.invoice && <DocDownloadButton payout={p} kind="invoice" label="Fattura" />}
                      {p.signedReceipt && <DocDownloadButton payout={p} kind="signed-receipt" label="Firmata" />}
                      {isSuperAdmin && p.signedReceipt && <VerifySignedReceiptToggle payout={p} />}
                      {p.status === 'pending_payment' && <AdminMarkPaidInline payout={p} />}
                      {canCancel && CANCELLABLE_STATUSES.includes(p.status) && <AdminCancelInline payout={p} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 border-t border-neutral-200 px-5 py-3">
          <button
            disabled={!pagination.hasPrevPage}
            onClick={() => setFilters({ page: String(page - 1) })}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Precedente
          </button>
          <span className="px-4 text-sm text-neutral-400">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            disabled={!pagination.hasNextPage}
            onClick={() => setFilters({ page: String(page + 1) })}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Successiva
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function PayoutsAdminPage() {
  const { data: summaries, isLoading, isError } = useEarningsSummary();
  const createRun = useCreatePayoutRun();

  // Map professionalId → pending payout just created (so we can show "Segna pagato")
  const [pendingPayouts, setPendingPayouts] = useState<Record<string, Payout>>({});
  // Which professionalId currently shows the mark-paid form
  const [markPaidOpen, setMarkPaidOpen] = useState<string | null>(null);
  // Per-row error for "Crea payout"
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const handleCreateRun = (professionalId: string) => {
    setRowErrors((prev) => ({ ...prev, [professionalId]: '' }));
    createRun.mutate(professionalId, {
      onSuccess: (payout) => {
        setPendingPayouts((prev) => ({ ...prev, [professionalId]: payout }));
        setMarkPaidOpen(professionalId);
      },
      onError: (err) => {
        setRowErrors((prev) => ({
          ...prev,
          [professionalId]: extractErrorMessage(err),
        }));
      },
    });
  };

  const handleMarkPaidDismiss = (professionalId: string) => {
    setMarkPaidOpen(null);
    setPendingPayouts((prev) => {
      const next = { ...prev };
      delete next[professionalId];
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Compensi</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Compensi generati (proforma / ritenuta), conferme dei professionisti e pagamenti
        </p>
      </div>

      {/* Generated payouts list with confirm/pay flow */}
      <CompensiList />

      {/* Manual run: pending earnings not yet turned into a payout */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">Compensi in sospeso (generazione manuale)</h2>
        <p className="mt-0.5 text-sm text-neutral-600">
          La generazione è automatica il 16 e l'ultimo giorno del mese. Qui puoi forzarla manualmente.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          Impossibile caricare il riepilogo dei compensi. Riprova più tardi.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Professionista
                  </th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Compensi in sospeso
                  </th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Numero compensi
                  </th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Più vecchio
                  </th>
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Già pagato
                  </th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(summaries ?? []).map((row) => {
                  const isCreating =
                    createRun.isPending && createRun.variables === row.professionalId;
                  const rowError = rowErrors[row.professionalId];
                  const pendingPayout = pendingPayouts[row.professionalId];

                  return (
                    <tr key={row.professionalId} className="transition-colors hover:bg-neutral-50/50">
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-semibold text-neutral-900">
                            {row.professionalName ?? 'Professionista'}
                          </span>
                          {row.professionalEmail && (
                            <span className="text-xs text-neutral-500">{row.professionalEmail}</span>
                          )}
                          <span className="font-mono text-xs text-neutral-400">
                            ID: {row.professionalId}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm font-semibold text-neutral-900">
                          {formatEur(row.pendingTotal)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm text-neutral-700">{row.pendingCount}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-neutral-600">
                        {formatDate(row.oldestPendingAt)}
                      </td>
                      <td className="px-5 py-3.5 text-right text-sm text-neutral-500">
                        {formatEur(row.paidTotal)}
                      </td>
                      <td className="px-5 py-3.5">
                        {pendingPayout && markPaidOpen === row.professionalId ? (
                          <MarkPaidForm
                            payout={pendingPayout}
                            onDismiss={() => handleMarkPaidDismiss(row.professionalId)}
                          />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleCreateRun(row.professionalId)}
                              disabled={isCreating || row.pendingCount === 0}
                              className="rounded-lg bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isCreating ? (
                                <span className="flex items-center gap-1.5">
                                  <Spinner size="sm" />
                                  Creazione…
                                </span>
                              ) : (
                                'Crea payout'
                              )}
                            </button>
                            {rowError && (
                              <p className="text-xs text-red-600">{rowError}</p>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(summaries ?? []).length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                <svg
                  className="h-6 w-6 text-neutral-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"
                  />
                </svg>
              </div>
              <p className="mt-4 font-medium text-neutral-700">Nessun compenso in sospeso</p>
              <p className="mt-1 text-sm text-neutral-600">
                I compensi in sospeso dei professionisti appariranno qui
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
